import {
  cloneElement,
  isValidElement,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type Ref,
  type RefObject,
} from "react";
import { handoffFocus, type FocusTarget } from "./focus-handoff";
import { motionMs, whenAnimationsDone, type MotionExit } from "./motion";

export type PresenceState = "open" | "closed";

export interface PresenceOptions {
  /**
   * The exit token the element plays (`base` = 130 ms): the wait gives up 100
   * ms after it. Without it, after the running animations' own end.
   */
  exit?: MotionExit;
  /** Leave at once, with no exit: the next screen takes over (a pick that navigates). */
  skip?: boolean;
  /**
   * Where the focus goes when the leaving element holds it (P15): an
   * element, a ref or a function, read as the close starts. Then the element
   * that had the focus when it opened (its trigger); never <body>.
   */
  focusTarget?: FocusTarget;
}

export interface PresenceHandle<T extends HTMLElement> {
  /** Render the overlay (open, or closing while its exit animation runs). */
  mounted: boolean;
  /** `open` went false and the exit animation is running. */
  closing: boolean;
  /** `data-state` for the element: `closed` while it leaves. */
  state: PresenceState;
  /** Attach to the element whose exit animation ends the presence. */
  ref: RefObject<T | null>;
  /** `ref` as a callback, to merge with the element's own ref. */
  attach: (node: T | null) => void;
}

/**
 * Something finite runs on the element: `whenAnimationsDone`'s own test, read
 * synchronously so nothing that does not animate (jsdom, hot mode,
 * `display: none`, `animation: none`) waits a microtask to leave.
 */
function animating(el: Element): boolean {
  if (typeof el.getAnimations === "function") {
    return el.getAnimations().some((a) => a.effect?.getTiming().iterations !== Infinity);
  }
  const name = typeof getComputedStyle === "function" ? getComputedStyle(el).animationName : "";
  return !!name && name !== "none";
}

/**
 * Keeps an overlay mounted after `open` goes false until its exit animation
 * (`data-state="closed"` classes) ends — "everything that enters also leaves"
 * (spec 07 › Movimiento). The state is derived while rendering (React's
 * documented pattern), so a closing overlay never misses a frame. When the
 * close starts (t0, before paint): the focus first leaves the element if it
 * held it (`handoffFocus` to `focusTarget`, then to the element focused when
 * it opened), then the element turns `inert` with `pointer-events: none`.
 * The end is `whenAnimationsDone` (never `animationend`, which hot mode,
 * `display: none` and 0 ms never fire): at once when nothing runs, as in jsdom.
 * Reopening while it leaves takes it back.
 */
export function usePresence<T extends HTMLElement>(open: boolean, options: PresenceOptions = {}): PresenceHandle<T> {
  const ref = useRef<T | null>(null);
  const opener = useRef<Element | null>(null);
  const latest = useRef(options);
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  // Adjust state while rendering (React's documented pattern for derived state).
  if (open && (!mounted || closing)) {
    setMounted(true);
    setClosing(false);
  } else if (!open && mounted && !closing) {
    setClosing(true);
  }

  useLayoutEffect(() => {
    latest.current = options;
  });

  // What had the focus when it opened: where the focus goes back.
  useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    if (active && active !== document.body && !ref.current?.contains(active)) opener.current = active;
  }, [open]);

  useLayoutEffect(() => {
    if (!closing) return;
    const el = ref.current;
    const { exit, skip, focusTarget } = latest.current;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setMounted(false);
      setClosing(false);
    };
    let restore = () => {};
    if (el) {
      // First the focus: an inert subtree drops it to <body>.
      handoffFocus(el, [focusTarget, opener.current]);
      const inert = el.hasAttribute("inert");
      const pointerEvents = el.style.pointerEvents;
      if (!inert) el.setAttribute("inert", "");
      el.style.pointerEvents = "none";
      restore = () => {
        if (!inert) el.removeAttribute("inert");
        el.style.pointerEvents = pointerEvents;
      };
    }
    if (skip || !el || !animating(el)) {
      finish();
      return restore;
    }
    const abort = new AbortController();
    void whenAnimationsDone(el, {
      signal: abort.signal,
      fallbackMs: exit ? motionMs(`${exit}-exit`) + 100 : undefined,
    }).then(finish);
    return () => {
      abort.abort();
      restore();
    };
  }, [closing]);

  const attach = useCallback((node: T | null) => {
    ref.current = node;
  }, []);

  const leaving = !open && closing;
  return { mounted: open || mounted, closing: leaving, state: leaving ? "closed" : "open", ref, attach };
}

export interface PresenceProps extends PresenceOptions {
  show: boolean;
  /**
   * Keep the last children (and their props) while it leaves: a sheet's
   * content, a confirmation's text or a count that the close already
   * cleared never changes on its way out.
   */
  latch?: boolean;
  /** One element that takes a ref; it gets `data-state` (`open` | `closed`) for its classes. */
  children: ReactElement;
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref) (ref as RefObject<T | null>).current = value;
}

/**
 * `usePresence` for one element (spec 07 › Movimiento): a badge, a dot, an
 * inline banner, a floating card. Its child enters with its own classes and
 * leaves with `data-[state=closed]:…` ones; small elements leave by opacity
 * only (P6).
 */
export function Presence({ show, latch = false, children, ...options }: PresenceProps) {
  const { mounted, state, attach } = usePresence<HTMLElement>(show, options);
  const [held, setHeld] = useState(children);
  if (latch && show && held !== children) setHeld(children);

  const child = latch && !show ? held : children;
  const childRef = isValidElement(child) ? (child.props as { ref?: Ref<HTMLElement> }).ref : undefined;
  const setRef = useCallback(
    (node: HTMLElement | null) => {
      attach(node);
      assignRef(childRef, node);
    },
    [attach, childRef],
  );

  if (!mounted || !isValidElement(child)) return null;
  return cloneElement(child as ReactElement<{ "data-state"?: PresenceState; ref?: Ref<HTMLElement> }>, {
    "data-state": state,
    ref: setRef,
  });
}
